import type { CSSProperties } from 'react';
import { css } from '@mildang/styled-system/css';
import { token } from '@mildang/styled-system/tokens';

/**
 * `iconColor` 는 **CSS 변수 한 겹을 거쳐야** 먹는다.
 *
 * Panda 는 zero-runtime 이라 `css({ '& svg': { color: iconColor } })` 처럼 값이 변수로 들어가면
 * 클래스명만 런타임에 만들어지고 규칙은 CSS 에 없다 — 빌드 타임에 그 조합을 정적 추출한
 * 파일이 있어야 하는데, prop 값은 호출자마다 다르므로 사실상 없다. 실측으로 ds-docs 의
 * `panda.css` 75,725 줄에 `[&_svg]:ids-c_*` 규칙은 **3개**뿐이었고(`brand.fill.base` ·
 * `neutral.fill.base` · `neutral.icon.low` — DateInput 이 리터럴로 쓴 값들), 그래서 그 셋을
 * 제외한 모든 `iconColor` 가 **에러도 경고도 없이 무시**됐다(사용자 실측: 문서 페이지에서
 * `iconColor="warning.icon.base"` 를 넣어도 아이콘이 회색).
 *
 * 그래서 **규칙은 리터럴 하나로 고정**(`color: var(--ids-button-icon-color)`)해 항상 추출되게
 * 두고, 값만 인라인 custom property 로 넘긴다 —
 * `packages/design-system/CLAUDE.md` "동적 스타일링" 의 해결 순서 2번(단일 값은 CSS 변수).
 *
 * Button/ToggleButton 등 `iconColor` 를 지원하는 버튼 계열이 이 헬퍼를 공유한다 — 각자
 * `{...restProps}` 로 하위(`ButtonBase`/`IconButtonBase`) 에 넘기기 전에 `iconColor` 를 top-level
 * destructuring 으로 반드시 소비해야 한다 (안 그러면 DOM 까지 새어 나간다).
 */
export const iconColorClass = css({ '& svg': { color: 'var(--ids-button-icon-color)' } });

export function iconColorCustomProperty(iconColor: string | undefined): CSSProperties | undefined {
  if (!iconColor) return undefined;
  const resolved = token.var(`colors.${iconColor}` as Parameters<typeof token.var>[0]) ?? iconColor;
  return { '--ids-button-icon-color': resolved } as CSSProperties;
}
